class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      cartItems.updateQuantity(this.dataset.index, 0, event);
    });
  }
}

const PW_SUBSCRIPTION_OFFER_ENDS_AT = Date.parse('2027-01-01T15:59:59Z');
const pwSubscriptionOfferActive = Number.isFinite(PW_SUBSCRIPTION_OFFER_ENDS_AT) && Date.now() < PW_SUBSCRIPTION_OFFER_ENDS_AT;
document.documentElement.classList.toggle('pw-subscription-offer-active', pwSubscriptionOfferActive);

document.addEventListener('click', async (event) => {
  const button = event.target.closest('[data-pw-subscription-upgrade]');
  if (!button || !pwSubscriptionOfferActive) return;
  event.preventDefault();
  if (button.disabled) return;

  const status = button.parentElement.querySelector('[data-pw-subscription-status]');
  const originalLabel = button.textContent;
  button.disabled = true;
  button.textContent = 'Switching…';
  if (status) status.textContent = '';

  try {
    const response = await fetch(routes.cart_change_url, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({
        id: button.dataset.lineKey,
        quantity: Number(button.dataset.quantity || 1),
        selling_plan: Number(button.dataset.sellingPlanId),
        sections: ['cart-drawer', 'cart-icon-bubble', 'main-cart-items', 'main-cart-footer'],
        sections_url: window.location.pathname,
      }),
    });
    const state = await response.json();
    if (!response.ok || state.errors) throw new Error(state.description || state.message || 'Unable to switch this item.');

    syncCartCountBubbleFromState(Number(state.item_count || 0));
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer?.renderContents && state.sections?.['cart-drawer'] && state.sections?.['cart-icon-bubble']) {
      cartDrawer.renderContents(state);
    } else {
      window.location.reload();
    }
  } catch (error) {
    button.disabled = false;
    button.textContent = originalLabel;
    if (status) status.textContent = error.message || 'Unable to switch this item. Please try again.';
  }
});

function pwDiscountApplicationCode(application) {
  const discount = application?.discount_application || application;
  return discount?.type === 'discount_code' ? String(discount.title || '').trim() : '';
}

function pwManualDiscountCodes(cartState) {
  return (cartState.cart_level_discount_applications || [])
    .map(pwDiscountApplicationCode)
    .filter(Boolean);
}

function pwUniqueDiscountCodes(codes) {
  const seen = new Set();
  return codes.filter((code) => {
    const key = code.toLowerCase();
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function pwDiscountContext(trigger) {
  const scope = trigger.closest('cart-drawer') || trigger.closest('#main-cart-footer') || document;
  return scope.querySelector('[data-pw-cart-discount]') || document.querySelector('[data-pw-cart-discount]');
}

function pwSetDiscountStatus(message, scope = document) {
  scope.querySelectorAll('[data-pw-cart-discount-status]').forEach((status) => {
    status.textContent = message;
  });
}

function pwReplaceDiscountSections(state) {
  const drawerMarkup = state.sections?.['cart-drawer'];
  if (typeof drawerMarkup === 'string') {
    const drawerDocument = new DOMParser().parseFromString(drawerMarkup, 'text/html');
    ['cart-drawer-items', '.cart-drawer__footer'].forEach((selector) => {
      const target = document.querySelector(`cart-drawer ${selector}`);
      const source = drawerDocument.querySelector(selector);
      if (target && source) target.replaceWith(source);
    });
  }

  const cartFooter = document.getElementById('main-cart-footer');
  const cartFooterSectionId = cartFooter?.dataset.id;
  const cartFooterMarkup = cartFooterSectionId ? state.sections?.[cartFooterSectionId] : null;
  if (typeof cartFooterMarkup === 'string') {
    const footerDocument = new DOMParser().parseFromString(cartFooterMarkup, 'text/html');
    const target = cartFooter.querySelector('.js-contents');
    const source = footerDocument.querySelector('.js-contents');
    if (target && source) target.replaceWith(source);
  }
}

async function pwUpdateCartDiscount(trigger, code, removeCode = false) {
  const context = pwDiscountContext(trigger);
  const statusScope = trigger.closest('cart-drawer') || trigger.closest('#main-cart-footer') || document;
  const originalLabel = trigger.textContent;
  const workingText = removeCode ? context?.dataset.removingText : context?.dataset.applyingText;
  trigger.disabled = true;
  trigger.setAttribute('aria-busy', 'true');
  if (workingText) trigger.textContent = workingText;
  pwSetDiscountStatus(workingText || '', statusScope);

  try {
    const cartResponse = await fetch(`${routes.cart_url}.js`, {
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    });
    const cartState = await cartResponse.json();
    if (!cartResponse.ok) throw new Error(window.cartStrings.error);

    const currentCodes = pwManualDiscountCodes(cartState);
    const normalizedCode = code.toLowerCase();
    const nextCodes = removeCode
      ? currentCodes.filter((currentCode) => currentCode.toLowerCase() !== normalizedCode)
      : pwUniqueDiscountCodes([...currentCodes, code]);
    const cartFooterSectionId = document.getElementById('main-cart-footer')?.dataset.id;
    const sections = pwUniqueDiscountCodes(['cart-drawer', cartFooterSectionId].filter(Boolean));

    const updateResponse = await fetch(routes.cart_update_url, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({
        discount: nextCodes.join(','),
        sections,
        sections_url: window.location.pathname,
      }),
    });
    const updatedState = await updateResponse.json();
    if (!updateResponse.ok || updatedState.errors) {
      throw new Error(updatedState.description || updatedState.message || context?.dataset.invalidText || window.cartStrings.error);
    }

    const appliedCodes = pwManualDiscountCodes(updatedState).map((appliedCode) => appliedCode.toLowerCase());
    const changeSucceeded = removeCode ? !appliedCodes.includes(normalizedCode) : appliedCodes.includes(normalizedCode);
    if (!changeSucceeded) throw new Error(context?.dataset.invalidText || window.cartStrings.error);

    pwReplaceDiscountSections(updatedState);
    pwSetDiscountStatus(removeCode ? context?.dataset.removedText || '' : context?.dataset.appliedText || '');
  } catch (error) {
    trigger.disabled = false;
    trigger.removeAttribute('aria-busy');
    trigger.textContent = originalLabel;
    pwSetDiscountStatus(error.message || context?.dataset.invalidText || window.cartStrings.error, statusScope);
  }
}

document.addEventListener('submit', (event) => {
  const form = event.target.closest('[data-pw-cart-discount-form]');
  if (!form) return;
  event.preventDefault();
  const input = form.querySelector('input[name="discount"]');
  const code = input?.value.trim();
  if (!code) {
    input?.reportValidity();
    return;
  }
  pwUpdateCartDiscount(form.querySelector('button[type="submit"]'), code);
});

document.addEventListener('click', (event) => {
  const button = event.target.closest('[data-pw-discount-remove]');
  if (!button) return;
  event.preventDefault();
  pwUpdateCartDiscount(button, button.dataset.pwDiscountRemove, true);
});

customElements.define('cart-remove-button', CartRemoveButton);

function syncCartCountBubbleFromState(itemCount) {
  const cartCountBubble = document.querySelector('.cart-count-bubble');
  if (itemCount === 0) {
    cartCountBubble?.remove();
    return;
  }
  if (!cartCountBubble) return;
  const visibleCount = cartCountBubble.querySelector('span[aria-hidden="true"]');
  const accessibleCount = cartCountBubble.querySelector('.visually-hidden');
  if (visibleCount) visibleCount.textContent = itemCount;
  if (accessibleCount) accessibleCount.textContent = `${itemCount} ${itemCount === 1 ? 'item' : 'items'}`;
}

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') {
        return;
      }
      return this.onCartUpdate();
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;
    let message = '';

    if (inputValue < event.target.dataset.min) {
      message = window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min);
    } else if (inputValue > parseInt(event.target.max)) {
      message = window.quickOrderListStrings.max_error.replace('[max]', event.target.max);
    } else if (inputValue % parseInt(event.target.step) !== 0) {
      message = window.quickOrderListStrings.step_error.replace('[step]', event.target.step);
    }

    if (message) {
      this.setValidity(event, index, message);
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.updateQuantity(
        index,
        inputValue,
        event,
        document.activeElement.getAttribute('name'),
        event.target.dataset.quantityVariantId
      );
    }
  }

  onChange(event) {
    this.validateQuantity(event);
  }

  onCartUpdate() {
    if (this.tagName === 'CART-DRAWER-ITEMS') {
      return fetch(`${routes.cart_url}?section_id=cart-drawer`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const selectors = ['cart-drawer-items', '.cart-drawer__footer'];
          for (const selector of selectors) {
            const targetElement = document.querySelector(selector);
            const sourceElement = html.querySelector(selector);
            if (targetElement && sourceElement) {
              targetElement.replaceWith(sourceElement);
            }
          }
        })
        .catch((e) => {
          console.error(e);
        });
    } else {
      return fetch(`${routes.cart_url}?section_id=main-cart-items`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const sourceQty = html.querySelector('cart-items');
          this.innerHTML = sourceQty.innerHTML;
        })
        .catch((e) => {
          console.error(e);
        });
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer').dataset.id,
        selector: '.js-contents',
      },
    ];
  }

  updateQuantity(line, quantity, event, name, variantId) {
    this.enableLoading(line);

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });
    const eventTarget = event.currentTarget instanceof CartRemoveButton ? 'clear' : 'change';

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);

        CartPerformance.measure(`${eventTarget}:paint-updated-sections"`, () => {
          const quantityElement =
            document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
          const items = document.querySelectorAll('.cart-item');

          if (parsedState.errors) {
            quantityElement.value = quantityElement.getAttribute('value');
            this.updateLiveRegions(line, parsedState.errors);
            return;
          }

          this.classList.toggle('is-empty', parsedState.item_count === 0);
          const cartDrawerWrapper = document.querySelector('cart-drawer');
          const cartFooter = document.getElementById('main-cart-footer');

          if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
          if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

          this.getSectionsToRender().forEach((section) => {
            const sectionRoot = document.getElementById(section.id);
            if (!sectionRoot) return;

            const elementToReplace = sectionRoot.querySelector(section.selector) || sectionRoot;
            const sectionHTML = parsedState.sections?.[section.section];
            const replacementHTML =
              typeof sectionHTML === 'string' ? this.getSectionInnerHTML(sectionHTML, section.selector) : '';

            if (replacementHTML) elementToReplace.innerHTML = replacementHTML;
          });
          syncCartCountBubbleFromState(parsedState.item_count);
          window.setTimeout(() => syncCartCountBubbleFromState(parsedState.item_count), 150);

          if (parsedState.item_count === 0 && cartDrawerWrapper) {
            cartDrawerWrapper.close();
            fetch(`${routes.cart_url}?section_id=cart-drawer&_=${Date.now()}`, {
              cache: 'no-store',
              headers: { Accept: 'text/html' },
            })
              .then((response) => {
                if (!response.ok) throw new Error(`Cart drawer refresh failed (${response.status})`);
                return response.text();
              })
              .then((responseText) => {
                const sourceDrawer = new DOMParser()
                  .parseFromString(responseText, 'text/html')
                  .querySelector('cart-drawer');
                if (!sourceDrawer) throw new Error('Cart drawer refresh returned no drawer');

                cartDrawerWrapper.innerHTML = sourceDrawer.innerHTML;
                cartDrawerWrapper.classList.add('is-empty');
                cartDrawerWrapper
                  .querySelector('#CartDrawer-Overlay')
                  ?.addEventListener('click', cartDrawerWrapper.close.bind(cartDrawerWrapper));
              })
              .catch(() => {
                window.location.assign(routes.cart_url);
              });
          }

          const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
          let message = '';
          if (items.length === parsedState.items.length && updatedValue !== parseInt(quantityElement.value)) {
            if (typeof updatedValue === 'undefined') {
              message = window.cartStrings.error;
            } else {
              message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
            }
          }
          this.updateLiveRegions(line, message);

          const lineItem =
            document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
          if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
            cartDrawerWrapper
              ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`))
              : lineItem.querySelector(`[name="${name}"]`).focus();
          } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
            trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'));
          } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
            trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'));
          }
        });

        CartPerformance.measureFromEvent(`${eventTarget}:user-action`, event);

        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId: variantId });
      })
      .catch(() => {
        this.querySelectorAll('.loading__spinner').forEach((overlay) => overlay.classList.add('hidden'));
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) lineItemError.querySelector('.cart-item__error-text').textContent = message;

    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus =
      document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    const source = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return source ? source.innerHTML : '';
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.add('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove('hidden'));

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.remove('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    cartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
    cartDrawerItemElements.forEach((overlay) => overlay.classList.add('hidden'));
  }
}

customElements.define('cart-items', CartItems);

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          'input',
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } })
              .then(() => CartPerformance.measureFromEvent('note-update:user-action', event));
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}
